import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css, Global } from '@emotion/react';
import { Dropdown } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import { RootState } from 'redux/schemas';
import { AiRolePlaySubmission } from 'redux/schemas/models/ai-role-play';
import {
  getAiRolePlaySubmission,
  getUserSubmissions,
  getUserSubmissionIds,
} from 'redux/selectors/timeline';
import { getAiRolePlaySubmissionsList } from 'redux/actions/ai-role-play';
import { AngularContext } from 'react-app';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';

import NvDropdown, {
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import {
  standardSpacing,
  quarterSpacing,
  threeQuartersSpacing,
  confettiZIndex,
} from 'styles/global_defaults/scaffolding';
import { info } from 'styles/global_defaults/colors';
import LoadingPlaceholder, { LoaderType } from 'shared/components/loading-placeholder';
import SubmissionEvaluationStatus from './submission-evaluation-status';

const PAGE_SIZE = 10;
const SCROLL_PADDING = 40;

const rowStyles = (isSelected: boolean) => css`
  padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
  gap: ${standardSpacing}px;
  cursor: pointer;
  min-width: 320px;
  background: ${isSelected ? info : 'transparent'};

  &:hover {
    background: ${info};
  }
`;

type RowProps = {
  submissionId: number;
  currentSubmissionId: number;
  rolePlayId: number;
};

const SubmissionRowContent = ({ submissionId, currentSubmissionId, rolePlayId }: RowProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const submission = useSelector((state: RootState) => getAiRolePlaySubmission(state, submissionId));

  if (!submission) return null;

  const date = moment(submission.finishedAt || submission.createdAt).format('MMM D, YYYY h:mmA');
  const isSelected = submissionId === currentSubmissionId;

  function handleClick() {
    $state.go('role-play-submission-modal', { rolePlayId, section: submissionId });
  }

  return (
    <Dropdown.Item
      className='p-0'
      as='button'
      onClick={handleClick}
    >
      <div
        className='d-flex align-items-center justify-content-between'
        css={rowStyles(isSelected)}
      >
        <span className='text-regular'>{date}</span>
        <SubmissionEvaluationStatus evaluation={submission.feedback?.evaluation} />
      </div>
    </Dropdown.Item>
  );
};

type Props = {
  submission: AiRolePlaySubmission;
  rolePlayId: number;
  catalogId: string;
};

const UserSubmissionDropdown = ({ submission, rolePlayId, catalogId }: Props) => {
  const dispatch = useAppDispatch();
  const userId = submission.user?.id;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [page, setPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);

  const listState = useSelector((state: RootState) => getUserSubmissions(state, rolePlayId, userId));
  const submissionIds = useSelector((state: RootState) => getUserSubmissionIds(state, rolePlayId, userId));

  const isLoading = !!listState?.isLoading;
  const hasMore = listState ? listState.hasMore : true;

  // Written during render so the effect always reads the latest value without
  // adding listState to its deps (which would cause a fetch loop).
  const isOnceLoadedRef = useRef(false);
  isOnceLoadedRef.current = !!listState?.isOnceLoaded;

  useEffect(() => {
    setPage(1);
  }, [userId, rolePlayId]);

  useEffect(() => {
    if (!userId || !catalogId || (page === 1 && isOnceLoadedRef.current)) return undefined;
    const request = dispatch(getAiRolePlaySubmissionsList({
      catalogId,
      aiRolePlayId: rolePlayId,
      page,
      pageSize: PAGE_SIZE,
      userId,
    }));
    return () => { request.abort(); };
  }, [page, userId, rolePlayId, catalogId, dispatch]);

  const isScrolledToBottom = useInfiniteScroll(menuElement, SCROLL_PADDING);

  useEffect(() => {
    if (menuElement && isScrolledToBottom && !isLoading && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [menuElement, isScrolledToBottom, isLoading, hasMore]);

  const handleToggle = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  useEffect(() => {
    setMenuElement(isOpen ? dropdownRef.current?.querySelector<HTMLElement>('.user-submissions-menu') ?? null : null);
  }, [isOpen]);

  const items: NvDropdownOption[] = useMemo(() => {
    const rows: NvDropdownOption[] = submissionIds.map((id) => ({
      type: 'custom' as const,
      id,
      customItem: (
        <SubmissionRowContent
          submissionId={id}
          currentSubmissionId={submission.id}
          rolePlayId={rolePlayId}
        />
      ),
    }));

    if (isLoading) {
      rows.push({
        type: 'custom' as const,
        id: 'loading',
        preventClosing: true,
        customItem: (
          <div className='p-2' css={css`width: 320px;`}>
            <LoadingPlaceholder loaderType={LoaderType.HALF} />
          </div>
        ),
      });
    }

    return rows;
  }, [submissionIds, submission.id, isLoading, rolePlayId]);

  const title = moment(submission.finishedAt || submission.createdAt).format('MMM D, YYYY h:mmA');

  return (
    <div className='d-flex align-items-center'>
      <Global styles={css`
        .user-submissions-button {
          min-width: 200px;
        }
        .user-submissions-menu {
          max-height: 280px;
          overflow-y: auto;
          z-index: ${confettiZIndex + 2};
        }
      `}
      />
      <p className='mb-0 mr-2 text-regular'>
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.FEEDBACK_FOR()}
      </p>
      <NvDropdown
        ref={dropdownRef}
        buttonStyle={NvDropdownButtonStyle.FORM}
        title={title}
        items={items}
        menuClassName='user-submissions-menu'
        buttonClass='user-submissions-button'
        onToggle={handleToggle}
      />
    </div>
  );
};

export default UserSubmissionDropdown;
